/**
 * =============================================================================
 * Componente: AffiliateValidationStep
 * Versão: 2.0.0
 *
 * Descrição:
 *   Etapa 2 — exibe estatísticas e a tabela de linhas validadas, permitindo
 *   edição inline de nome/e-mail/telefone antes de avançar. O botão de avançar
 *   foi movido para o rodapé fixo do wizard (`ImportAffiliatesWizard`).
 *
 * CHANGELOG:
 *   2026-05-17 v2.0.0 - Adota KpiCard unificado, layout flex h-full com
 *                       tabela rolável (horizontal + vertical) em área
 *                       contida, e remove botão "Continuar" interno (passa
 *                       a viver no rodapé do wizard). Atende contrato
 *                       wizard-step-layout-contract.
 *   2026-05-17 v1.0.0 - Versão inicial.
 * =============================================================================
 */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertCircle,
  AlertTriangle,
  RefreshCw,
  Users,
  UserPlus,
  UserCheck,
  XCircle,
  Loader2,
  Copy,
} from 'lucide-react';

import { KpiCard } from '@/components/layout/list-page/KpiCard';
import { AffiliateCSVParseResult, AffiliateRowValidation } from '@/lib/affiliate-csv-utils';
import { AffiliateValidationStats, AffiliateImportProgress } from '@/hooks/useAffiliateImport';

interface Props {
  csvData: AffiliateCSVParseResult | null;
  validatedRows: AffiliateRowValidation[];
  validationStats: AffiliateValidationStats | null;
  isValidating: boolean;
  progress: AffiliateImportProgress | null;
  onUpdateRow: (rowIndex: number, field: string, value: string) => void;
  onRevalidate: () => void;
  /**
   * Mantido por compatibilidade com a assinatura anterior; o botão real de
   * "Continuar" agora é renderizado pelo rodapé do `ImportAffiliatesWizard`.
   */
  onComplete?: () => void;
}

export default function AffiliateValidationStep({
  validatedRows,
  validationStats,
  isValidating,
  progress,
  onUpdateRow,
  onRevalidate,
}: Props) {
  const [showOnlyErrors, setShowOnlyErrors] = useState(false);
  const [editingCell, setEditingCell] = useState<{ row: number; field: string } | null>(null);

  const hasInvalid = validatedRows.some((v) => !v.isValid);
  const displayRows = showOnlyErrors ? validatedRows.filter((v) => !v.isValid) : validatedRows;

  // Tela de loading durante validação por chunks
  if (isValidating && progress) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-0 space-y-4">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
        <p className="font-medium">{progress.message}</p>
        <p className="text-sm text-muted-foreground">
          {progress.current} de {progress.total}
        </p>
        <Progress value={(progress.current / progress.total) * 100} className="w-64" />
      </div>
    );
  }

  return (
    // Container raiz da step — segue o contrato wizard-step-layout:
    // flex coluna ocupando 100% da altura, sem overflow externo. A área
    // de tabela é a única que rola (horizontal + vertical) internamente.
    <div className="flex flex-col gap-4 h-full min-h-0 w-full min-w-0">
      {/* KPIs unificados (padrão KpiCard) */}
      {validationStats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 flex-shrink-0 w-full min-w-0">
          <KpiCard
            color="blue"
            icon={Users}
            label="Total"
            value={validationStats.total}
          />
          <KpiCard
            color="green"
            icon={UserPlus}
            label="Novos"
            value={validationStats.newAffiliates}
          />
          <KpiCard
            color="cyan"
            icon={UserCheck}
            label="Já cadastrados"
            value={validationStats.existingInSystem}
          />
          <KpiCard
            color="red"
            icon={XCircle}
            label="Com erros"
            value={validationStats.invalid}
          />
        </div>
      )}

      {/* Barra de ações (filtro de erros + revalidar) */}
      <div className="flex items-center justify-between flex-wrap gap-3 flex-shrink-0">
        <div className="flex items-center gap-2">
          <Checkbox
            id="show-errors"
            checked={showOnlyErrors}
            onCheckedChange={(c) => setShowOnlyErrors(c === true)}
          />
          <label htmlFor="show-errors" className="text-sm">
            Mostrar apenas erros
          </label>
        </div>
        <Button variant="outline" size="sm" onClick={onRevalidate}>
          <RefreshCw className="h-4 w-4 mr-2" /> Revalidar
        </Button>
      </div>

      {/* Alertas contextuais */}
      {hasInvalid && (
        <Alert variant="destructive" className="flex-shrink-0">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Existem linhas com erros de formato. Corrija os problemas (ou remova essas linhas
            do CSV) para continuar.
          </AlertDescription>
        </Alert>
      )}

      {validationStats && validationStats.duplicatesInCSV > 0 && (
        <Alert variant="destructive" className="flex-shrink-0">
          <Copy className="h-4 w-4" />
          <AlertDescription>
            <strong>{validationStats.duplicatesInCSV}</strong> linha(s) com e-mail duplicado
            dentro do CSV. Cada e-mail deve aparecer apenas uma vez.
          </AlertDescription>
        </Alert>
      )}

      {validationStats && validationStats.existingInSystem > 0 && (
        <Alert className="flex-shrink-0">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <strong>{validationStats.existingInSystem}</strong> e-mail(s) já existem como
            afiliado neste tenant e serão <strong>ignorados</strong> na importação.
          </AlertDescription>
        </Alert>
      )}

      {/*
        Área da tabela — ÚNICA região rolável da step.
        - `flex-1 min-h-0` garante que ocupa o espaço restante sem estourar o pai.
        - `overflow-auto` oferece rolagem REAL nos dois eixos.
        - `<Table min-w-[900px]>` força scroll horizontal quando o viewport é estreito.
      */}
      <div className="flex-1 min-h-0 w-full min-w-0 border rounded-md overflow-auto">
        <Table className="min-w-[900px]">
          <TableHeader className="sticky top-0 bg-background z-10">
            <TableRow>
              <TableHead className="w-12 whitespace-nowrap">#</TableHead>
              <TableHead className="w-28 whitespace-nowrap">Status</TableHead>
              <TableHead className="min-w-[180px] whitespace-nowrap">Nome</TableHead>
              <TableHead className="min-w-[240px] whitespace-nowrap">E-mail</TableHead>
              <TableHead className="min-w-[160px] whitespace-nowrap">Telefone</TableHead>
              <TableHead className="min-w-[200px] whitespace-nowrap">Erros</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {displayRows.map((v) => {
              const errorStyle = !v.isValid;
              return (
                <TableRow key={v.rowIndex} className={errorStyle ? 'bg-destructive/5' : ''}>
                  <TableCell className="text-muted-foreground">{v.rowIndex + 2}</TableCell>
                  <TableCell>
                    {!v.isValid ? (
                      <Badge variant="destructive">Erro</Badge>
                    ) : v.existsInSystem ? (
                      <Badge variant="secondary">Existe</Badge>
                    ) : (
                      <Badge variant="default">Novo</Badge>
                    )}
                  </TableCell>
                  {(['nome', 'email', 'telefone'] as const).map((field) => (
                    <TableCell key={field}>
                      {editingCell?.row === v.rowIndex && editingCell.field === field ? (
                        <Input
                          value={v.row[field] || ''}
                          onChange={(e) => onUpdateRow(v.rowIndex, field, e.target.value)}
                          onBlur={() => setEditingCell(null)}
                          onKeyDown={(e) => e.key === 'Enter' && setEditingCell(null)}
                          autoFocus
                          className="h-8"
                        />
                      ) : (
                        <span
                          className="cursor-pointer hover:underline"
                          onClick={() => setEditingCell({ row: v.rowIndex, field })}
                        >
                          {v.row[field] || '-'}
                        </span>
                      )}
                    </TableCell>
                  ))}
                  <TableCell>
                    {v.errors.length > 0 && (
                      <span className="text-sm text-destructive">{v.errors.join('; ')}</span>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
